"use client";

import React, { useState } from "react";
import Image from "next/image";
import { NavigationProvider, useNavigation } from "@/lib/navigation/NavigationProvider";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useFeatureFlag } from "@/lib/tenant/TenantSettingsProvider";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import { AdSlot } from "@/features/ads/AdSlot";
import { ArticleCarousel } from "@/views/home/partials/articles/ArticleCarousel";
import { LatestArticlesSidebar } from "@/views/home/partials/articles/LatestArticlesSidebar";
import { PopularMatchesTicker } from "../Classic/components/PopularMatchesTicker";
import { HomeLivescoreProvider } from "../Classic/components/HomeLivescoreProvider";
import { NewsBlogCompetitionSection } from "../Classic/components/NewsBlogCompetitionSection";
import { CategorySection } from "@/views/home/parts/CategorySection";
import { PromoSection } from "@/views/home/parts/PromoSection";
import type { NewsBlogHomeClassicData, NewsBlogHomeSection } from "../Classic/types";
import { SectionStrip } from "@/components/layout/SectionStrip";
import type { BrandedStyle } from "@/components/layout/SectionStrip";
import { HOME_HERO, HOME_MATCHES, HOME_COMPETITIONS } from "@/features/theme/sectionKeys";
import { LiveScoreWidget } from "../Classic/components/LiveScoreWidget";
import type { ResolvedPromoSlot } from "@/features/promo-slots/types";

export interface NewsBlogHomeEditorialLayoutProps {
    data: NewsBlogHomeClassicData;
    videoShortsSlot?: React.ReactNode;
    videoHighlightsSlot?: React.ReactNode;
    sectionStyles?: Record<string, BrandedStyle>;
}

/**
 * NewsBlogHomeEditorialLayout - Magazine-style layout for content-rich news sites.
 *
 * Sections:
 * 1. Livescore Ticker (if enabled)
 * 2. Article Carousel (5 featured) + Latest Articles sidebar
 * 3. Promo 0
 * 4. Tabbed Competition Sections (Classic layout per tab: pyramid + standings + matches)
 * 5. Video Shorts
 * 6. Video Highlights
 * 7. Promo 1
 */
export function NewsBlogHomeEditorialLayout({
    data,
    videoShortsSlot,
    videoHighlightsSlot,
    sectionStyles,
}: NewsBlogHomeEditorialLayoutProps) {
    const labels = useLabels();
    const isLivescoreEnabled = useFeatureFlag("livescore");
    const nav = useNavigation();

    // Carousel: featured posts first, fallback to latest
    const carouselArticles =
        data.featuredPosts.length > 0 ? data.featuredPosts.slice(0, 5) : data.latestNews.slice(0, 5);

    // Sidebar: latest articles (no overlap with carousel when using featured)
    const latestStartIndex = data.featuredPosts.length > 0 ? 0 : 5;
    const sidebarArticles = data.latestNews.slice(latestStartIndex, latestStartIndex + 7);

    const content = (
        <div className="min-h-screen bg-background">
            <main className="max-w-350 mx-auto px-4 py-6 space-y-10">
                {/* Ad: content-top */}
                <AdSlot id="content-top" />

                {/* Popular Matches Ticker */}
                {isLivescoreEnabled && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_MATCHES]}>
                        <PopularMatchesTicker popularCompetitionIds={data.popularCompetitionIds} ticker={data.ticker} />
                    </SectionStrip>
                )}

                {/* Hero: Article Carousel + Latest Sidebar */}
                {carouselArticles.length > 0 && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_HERO]}>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                            <div className="lg:col-span-2">
                                <ArticleCarousel articles={carouselArticles} />
                            </div>
                            <div className="lg:col-span-1">
                                <LatestArticlesSidebar articles={sidebarArticles} title={labels.homePage.latestNews} />
                            </div>
                        </div>
                    </SectionStrip>
                )}

                {/* Promo slot 0 */}
                <PromoSlot row={data.promos[0] ?? null} />

                {/* Video Shorts */}
                {videoShortsSlot}

                {/* Live Score Widget */}
                {isLivescoreEnabled && (
                    <LiveScoreWidget
                        popularCompetitionIds={data.popularCompetitionIds}
                        livescoreUrl={nav.livescore()}
                    />
                )}

                {/* Ad: mid-scroll-1 */}
                <AdSlot id="mid-scroll-1" />

                {/* Content stream — tabbed entity runs separated by inline promos */}
                {data.sections.length > 0 && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_COMPETITIONS]}>
                        <EditorialContentStream sections={data.sections} />
                    </SectionStrip>
                )}

                {/* Ad: mid-scroll-2 */}
                <AdSlot id="mid-scroll-2" />

                {/* Video Highlights */}
                {videoHighlightsSlot}

                {/* Promo slot 1 */}
                <PromoSlot row={data.promos[1] ?? null} />

                {/* Ad: content-bottom */}
                <AdSlot id="content-bottom" />
            </main>
        </div>
    );

    if (isLivescoreEnabled) {
        return (
            <HomeLivescoreProvider locale={data.locale} initialBoard={data.livescoreBoard}>
                {content}
            </HomeLivescoreProvider>
        );
    }
    return content;
}

type EditorialBlock =
    | { kind: "promo"; promo: ResolvedPromoSlot }
    | { kind: "tabs"; entities: Extract<NewsBlogHomeSection, { kind: "competition" | "category" }>[] };

/**
 * Split mixed sections into renderable blocks. Promos break the tab run —
 * `[comp, cat, promo, comp]` becomes `[tabs(comp, cat), promo, tabs(comp)]`.
 */
function chunkSectionsByPromo(sections: NewsBlogHomeSection[]): EditorialBlock[] {
    const blocks: EditorialBlock[] = [];
    let buffer: Extract<NewsBlogHomeSection, { kind: "competition" | "category" }>[] = [];
    const flush = () => {
        if (buffer.length > 0) {
            blocks.push({ kind: "tabs", entities: buffer });
            buffer = [];
        }
    };
    for (const section of sections) {
        if (section.kind === "promo") {
            flush();
            blocks.push({ kind: "promo", promo: section.data });
        } else {
            buffer.push(section);
        }
    }
    flush();
    return blocks;
}

/**
 * EditorialContentStream - Renders the mixed homepage sections in the editorial variant.
 * Entity sections (competition + category) within each run share a tab strip; promos
 * render inline as block-level breaks between runs.
 */
function EditorialContentStream({ sections }: { sections: NewsBlogHomeSection[] }) {
    const blocks = chunkSectionsByPromo(sections);
    if (blocks.length === 0) return null;

    return (
        <div className="space-y-10">
            {blocks.map((block, i) => {
                if (block.kind === "promo") {
                    return <PromoSection key={`promo-${i}`} promo={block.promo} />;
                }
                return <TabbedHomeSections key={`tabs-${i}`} entities={block.entities} />;
            })}
        </div>
    );
}

/**
 * TabbedHomeSections - Tab strip across heterogeneous entity sections (competition + category).
 * Active tab dispatches by section kind to the appropriate full-section layout.
 */
function TabbedHomeSections({
    entities,
}: {
    entities: Extract<NewsBlogHomeSection, { kind: "competition" | "category" }>[];
}) {
    const [activeIndex, setActiveIndex] = useState(0);
    const active = entities[activeIndex];
    if (!active) return null;

    return (
        <section>
            {/* Tab bar */}
            <div className="flex gap-1 overflow-x-auto pb-2 mb-2 scrollbar-hide">
                {entities.map((section, index) => {
                    const label = section.kind === "competition" ? section.data.label : section.data.title;
                    const logo = section.kind === "competition" ? section.data.logo : section.data.badgeUrl;
                    const key =
                        section.kind === "competition"
                            ? `comp-${section.data.nativeId}-${index}`
                            : `cat-${section.data.categoryId}-${index}`;
                    return (
                        <button
                            key={key}
                            onClick={() => setActiveIndex(index)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors shrink-0 cursor-pointer ${
                                index === activeIndex
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                            }`}
                        >
                            {logo && (
                                <Image
                                    src={logo}
                                    alt={label}
                                    width={20}
                                    height={20}
                                    className="w-5 h-5 rounded-sm object-contain"
                                />
                            )}
                            {label}
                        </button>
                    );
                })}
            </div>

            {/* Active section */}
            {active.kind === "competition" ? (
                <NavigationProvider competitionId={active.data.nativeId}>
                    <NewsBlogCompetitionSection data={active.data} />
                </NavigationProvider>
            ) : (
                <CategorySection data={active.data} />
            )}
        </section>
    );
}
